/* Seed template for a brand-new (unconfigured) voice/video-call policy. Rendered when no
   policy row exists yet (exists=false) so the operator starts from a sensible, editable
   template instead of blank fields — priceable call buckets, an availability string, and
   the strategy / pricing-guidance prompts. Nothing is persisted until the operator saves,
   and the mode stays 'handoff' (the backend's missing-row default) so seeding a template
   never switches the account into automation. Also feeds the "Load examples" button so
   both share one source of truth. */
import type { CallPolicyBody, CallPricings } from '../../../api/client';

type Translate = (key: string) => string;

// The bucket key is composed as `<modality> <duration>`. The modality prefix MUST stay the
// literal 'voice'/'video' the CallPricing editor parses on (splitKey), so only the duration
// is translated. Prices are language-agnostic.
const CALL_BUCKETS: Array<{ modality: 'voice' | 'video'; durKey: string; price: number; floor: number }> = [
  { modality: 'voice', durKey: 'defaults.dur.d10', price: 30, floor: 20 },
  { modality: 'voice', durKey: 'defaults.dur.d20', price: 55, floor: 40 },
  { modality: 'video', durKey: 'defaults.dur.d10', price: 70, floor: 50 },
  { modality: 'video', durKey: 'defaults.dur.d20', price: 110, floor: 80 },
  { modality: 'video', durKey: 'defaults.dur.d30', price: 150, floor: 110 },
];

export function defaultCallPricings(t: Translate): CallPricings {
  const out: CallPricings = {};
  for (const b of CALL_BUCKETS) {
    const dur = t(b.durKey).trim();
    if (dur) out[`${b.modality} ${dur}`] = { price: b.price, floor: b.floor };
  }
  return out;
}

export function defaultCallBody(t: Translate): CallPolicyBody {
  return {
    is_active: true,
    // Template only — matches the backend's missing-row default ('handoff'); the operator
    // opts into automate. Never auto-enable just because we pre-filled fields.
    call_request_mode: 'handoff',
    call_pricings: defaultCallPricings(t),
    call_availability: t('defaults.availability'),
    pricing_guidance: t('defaults.pricingGuidance'),
    tip_tolerance: 2,
    max_negotiation_attempts: 3,
    strategy_prompt: t('defaults.strategyPrompt'),
    // Only consulted in 'decline' mode; seeded empty so a fresh template stays neutral.
    decline_note: null,
  };
}
